/**
 * Stockage Editor — Éditeur de Production avec Stockage
 *
 * Fork de cooling-system-2 avec ajout de stockages :
 * - Stockages centraux (drag-and-drop dans l'éditeur, entre prod et pompes)
 * - Stockages réseau (entités autonomes avec leur propre topologie)
 *
 * Fonctionnalités :
 * - 2 centrales : Bercy, Aubervilliers
 * - Canvas React Flow avec visualisation du réseau de production
 * - Sidebar avec 3 sections : Centrales, Composants, Stockage
 * - Composants drag-and-drop organisés par sections
 * - Édition via modales latérales (formulaires homogénéisés)
 * - Suppression avec confirmation
 */
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    NJButton,
    NJHeading,
    NJModal,
    NJModalContentInformation,
    NJText as FluidText,
    NJToastService,
    NJToastContainer,
    NJIcon,
    NJIconButton,
} from '@engie-group/fluid-design-system-react';
import { useReactFlow, type Edge } from '@xyflow/react';
import {
    ProductionEditorProvider,
    ProductionEditorCanvas,
    useProductionEditor,
    Asset,
    Contract,
    Group,
    GroupMember,
} from '@shared/production-editor';
import type { StorageNetworkFormData } from './types';
import type { PlantId, StorageNetworkId } from './graphs';
import {
    useDeleteNode,
    useStoragesNetwork,
    useCreateStorageNetwork,
    useUpdateStorageNetwork,
    useDeleteStorageNetwork,
} from './hooks';
import { buildGraphForPlant, buildGraphForStorageNetwork } from './graphs';
import { AnimatedFlowEdge } from './AnimatedEdge';
import { AnimatedFlowCyan } from './AnimatedFlowCyan';
import { AnimatedFlowOrange } from './AnimatedFlowOrange';
import { StorageNetworkEditModal } from './StorageNetworkEditModal';
import { ProductionLineEditModal, type ProductionLineFormData } from './ProductionLineEditModal';
import { PlantCreateModal } from './PlantCreateModal';
import { ChillerCreateModal } from './ChillerCreateModal';
import { PumpCreateModal } from './PumpCreateModal';
import { ContractCreateModal } from './ContractCreateModal';
import './flow-animation.css';

// Import des icônes SVG depuis le kit production-editor + storage icon
import departureIcon from '@shared/production-editor/icons/departure.svg';
import returnIcon from '@shared/production-editor/icons/return.svg';
import chillerIcon from '@shared/production-editor/icons/chiller.svg';
import pumpIcon from '@shared/production-editor/icons/pump.svg';
import waterIcon from '@shared/production-editor/icons/water.svg';
import powerIcon from '@shared/production-editor/icons/power.svg';
import waterCoolingIcon from '@shared/production-editor/icons/water_cooling.svg';
import storageIcon from './storage-icon.svg';

// ── Node Types ──────────────────────────────────────────────────────────
const CUSTOM_NODE_TYPES = {
    asset: Asset,
    contract: Contract,
    group: Group,
    groupMember: GroupMember,
};

// ── Edge Types ──────────────────────────────────────────────────────────
const CUSTOM_EDGE_TYPES = {
    animatedFlow: AnimatedFlowEdge,
    animatedFlowCyan: AnimatedFlowCyan,
    animatedFlowOrange: AnimatedFlowOrange,
};

// ── Meta ────────────────────────────────────────────────────────────────

export const meta = {
    title: 'Éditeur de Production avec Stockage',
    description:
        'Éditeur de production avec 2 centrales (Bercy, Aubervilliers) et gestion de stockages (centraux et réseau).',
    tags: ['editor', 'production', 'dashboard', 'po', 'designer'],
    author: 'stephane.coucke',
    status: 'draft' as const,
    category: 'production/editor',
};


type ViewMode = 'plant' | 'storage-network';

// ── TopBar Component (Barre supérieure avec navigation) ───────────────────

function TopBar() {
    return (
        <div
            style={{
                height: 56,
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                display: 'flex',
                alignItems: 'center',
                padding: '0 24px',
                gap: 32,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1001,
            }}
        >
            {/* Site selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
                <NJIcon name="flag" style={{ fontSize: 20, color: 'var(--nj-semantic-color-text-brand-primary-default)' }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>
                    Fast Life
                </span>
            </div>

            {/* Navigation tabs */}
            <div style={{ display: 'flex', gap: 0, flex: 1 }}>
                {[
                    { label: 'Éditeur de production', active: true },
                    { label: 'Électricité auxiliaires', active: false },
                    { label: 'Synthèse', active: false },
                ].map((tab) => (
                    <div
                        key={tab.label}
                        style={{
                            padding: '16px 20px',
                            borderBottom: tab.active ? '3px solid var(--nj-semantic-color-border-brand-primary-default)' : '3px solid transparent',
                            color: tab.active
                                ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                : 'var(--nj-semantic-color-text-neutral-secondary-default)',
                            fontSize: 14,
                            fontWeight: tab.active ? 600 : 400,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {tab.label}
                    </div>
                ))}
            </div>

            {/* Right actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div
                    style={{
                        padding: '6px 12px',
                        borderRadius: 4,
                        backgroundColor: 'var(--nj-semantic-color-background-neutral-secondary-default)',
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                        cursor: 'pointer',
                    }}
                >
                    Opti
                </div>
                <NJIcon
                    name="search"
                    style={{ fontSize: 20, color: 'var(--nj-semantic-color-text-neutral-secondary-default)', cursor: 'pointer' }}
                />
                <NJIcon
                    name="notifications"
                    style={{ fontSize: 20, color: 'var(--nj-semantic-color-text-neutral-secondary-default)', cursor: 'pointer' }}
                />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>
                        FR
                    </span>
                    <NJIcon name="expand_more" style={{ fontSize: 18 }} />
                </div>
            </div>
        </div>
    );
}

// ── Footer Component (Barre inférieure avec liens) ─────────────────────────

function Footer() {
    return (
        <div
            style={{
                height: 40,
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 24px',
                gap: 24,
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1001,
            }}
        >
            <a
                href="#"
                style={{
                    fontSize: 12,
                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                    textDecoration: 'none',
                }}
            >
                Contacter le support
            </a>
            <a
                href="#"
                style={{
                    fontSize: 12,
                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                    textDecoration: 'none',
                }}
            >
                Politique de confidentialité
            </a>
            <a
                href="#"
                style={{
                    fontSize: 12,
                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                    textDecoration: 'none',
                }}
            >
                Politique de cookies
            </a>
            {/* Logo ENGIE */}
            <div style={{ marginLeft: 16 }}>
                <svg width="52" height="16" viewBox="0 0 52 16" fill="none">
                    <text x="0" y="12" fontSize="12" fontWeight="600" fill="#00AAFF">ENGIE</text>
                </svg>
            </div>
        </div>
    );
}

// ── Icon Bar Component (Barre d'icônes ultra-fine à gauche) ───────────────

type SidebarTab = 'production' | 'topology' | 'general' | 'import-export' | 'plannings' | 'optimizations' | 'scheduler' | 'calibrations' | 'forecastings';

interface IconBarProps {
    activeTab: SidebarTab;
    onTabChange: (tab: SidebarTab) => void;
}

function IconBar({ activeTab, onTabChange }: IconBarProps) {
    const tabs = [
        { id: 'production' as SidebarTab, icon: 'insert_chart', label: 'Production' },
        { id: 'topology' as SidebarTab, icon: 'account_tree', label: 'Topology' },
        { id: 'general' as SidebarTab, icon: 'visibility', label: 'General' },
        { id: 'import-export' as SidebarTab, icon: 'sync_alt', label: 'Import/export' },
        { id: 'plannings' as SidebarTab, icon: 'event', label: 'Plannings' },
        { id: 'optimizations' as SidebarTab, icon: 'bar_chart', label: 'Optimizations' },
        { id: 'scheduler' as SidebarTab, icon: 'schedule', label: 'Scheduler' },
        { id: 'calibrations' as SidebarTab, icon: 'tune', label: 'Calibrations' },
        { id: 'forecastings' as SidebarTab, icon: 'trending_up', label: 'Forecastings' },
    ];

    return (
        <div
            style={{
                width: 60,
                height: 'calc(100vh - 96px)',
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop: 16,
                gap: 8,
                position: 'fixed',
                left: 0,
                top: 56,
                zIndex: 1000,
            }}
        >
            {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                    <div
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        title={tab.label}
                        style={{
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 4,
                            cursor: 'pointer',
                            backgroundColor: active
                                ? 'var(--nj-semantic-color-background-brand-secondary-default)'
                                : 'transparent',
                            color: active
                                ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                : 'var(--nj-semantic-color-text-neutral-secondary-default)',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <NJIcon name={tab.icon} />
                    </div>
                );
            })}
        </div>
    );
}

// ── Secondary Panel Component (Panneau de contenu à droite de IconBar) ────

interface SecondaryPanelProps {
    activeTab: SidebarTab;
    viewMode: ViewMode;
    selectedPlantId: PlantId | null;
    selectedStorageNetworkId: StorageNetworkId | null;
    onPlantSelect: (plantId: PlantId) => void;
    onStorageNetworkSelect: (storageId: StorageNetworkId) => void;
    onCreateStorageNetwork: () => void;
    onStorageNetworkEdit: (storageId: string) => void;
    onStorageNetworkDelete: (storageId: string) => void;
    onStorageNetworkRename: (storageId: string, newName: string) => void;
    onPlantRename: (plantId: PlantId, newName: string) => void;
    onPlantDelete: (plantId: PlantId) => void;
    storagesNetwork: { id: string; name: string }[];
    plants: { id: PlantId; label: string }[];
    setIsCreatePlantModalOpen: (open: boolean) => void;
}

function SecondaryPanel({
    activeTab,
    viewMode,
    selectedPlantId,
    selectedStorageNetworkId,
    onPlantSelect,
    onStorageNetworkSelect,
    onCreateStorageNetwork,
    onStorageNetworkEdit: _onStorageNetworkEdit,
    onStorageNetworkDelete,
    onStorageNetworkRename,
    onPlantRename,
    onPlantDelete,
    storagesNetwork,
    plants,
    setIsCreatePlantModalOpen,
}: SecondaryPanelProps) {
    const { draggedItemRef } = useProductionEditor();
    const [centralesOpen, setCentralesOpen] = useState(true);
    const [composantsOpen, setComposantsOpen] = useState(true);
    const [stockageOpen, setStockageOpen] = useState(true);
    const [editingPlantId, setEditingPlantId] = useState<PlantId | null>(null);
    const [editingStorageId, setEditingStorageId] = useState<string | null>(null);

    // Rendu différent selon l'onglet actif
    if (activeTab !== 'production') {
        return (
            <div
                style={{
                    width: 280,
                    height: 'calc(100vh - 96px)',
                    backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                    borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                    position: 'fixed',
                    left: 68,
                    top: 56,
                    zIndex: 999,
                    padding: 24,
                    overflow: 'auto',
                }}
            >
                <FluidText scale="lg" style={{ marginBottom: 16 }}>
                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('-', ' / ')}
                </FluidText>
                <FluidText scale="md" style={{ color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                    Cette section n'est pas encore implémentée dans ce prototype.
                </FluidText>
            </div>
        );
    }

    // Rendu pour l'onglet Production (contenu actuel de la sidebar)
    return (
        <div
            style={{
                width: 280,
                height: 'calc(100vh - 96px)',
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                position: 'fixed',
                left: 68,
                top: 56,
                zIndex: 999,
                overflow: 'auto',
                paddingTop: 16,
            }}
        >
            {/* Section 1: Centrales */}
            <div style={{ border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', margin: 8, borderRadius: 4, overflow: 'hidden' }}>
                <div
                    onClick={() => setCentralesOpen((o) => !o)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        height: 48,
                        cursor: 'pointer',
                        borderBottom: centralesOpen ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none',
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>Centrales</span>
                    <NJIcon name={centralesOpen ? 'expand_less' : 'expand_more'} />
                </div>
                {centralesOpen && (
                    <>
                        <div>
                            {plants.map((plant) => {
                                const active = viewMode === 'plant' && selectedPlantId === plant.id;
                                return (
                                    <div
                                        key={plant.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '8px 11px 8px 14px',
                                            background: active ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent',
                                            borderLeft: active ? '3px solid var(--nj-semantic-color-border-brand-primary-default)' : '3px solid transparent',
                                            fontSize: 14,
                                            fontWeight: active ? 600 : 400,
                                            color: active
                                                ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                                : 'var(--nj-semantic-color-text-neutral-primary-default)',
                                        }}
                                    >
                                        <EditableLabel
                                            value={plant.label}
                                            onSave={(newName) => onPlantRename(plant.id, newName)}
                                            onClick={() => onPlantSelect(plant.id)}
                                            isActive={active}
                                            forceEdit={editingPlantId === plant.id}
                                            onEditEnd={() => setEditingPlantId(null)}
                                        />
                                        <div style={{ display: 'flex', gap: 4, opacity: 0.7 }}>
                                            <NJIcon
                                                name="edit"
                                                style={{ fontSize: 18, cursor: 'pointer', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingPlantId(plant.id);
                                                }}
                                            />
                                            <NJIcon
                                                name="delete"
                                                style={{ fontSize: 18, cursor: 'pointer', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onPlantDelete(plant.id);
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                            <NJButton
                                label="Créer une centrale"
                                emphasis="bold"
                                scale="sm"
                                onClick={() => setIsCreatePlantModalOpen(true)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Section 2: Stockage réseau */}
            <div style={{ border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', margin: '0 8px 8px', borderRadius: 4, overflow: 'hidden' }}>
                <div
                    onClick={() => setStockageOpen((o) => !o)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        height: 48,
                        cursor: 'pointer',
                        borderBottom: stockageOpen ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none',
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>Stockage réseau</span>
                    <NJIcon name={stockageOpen ? 'expand_less' : 'expand_more'} />
                </div>
                {stockageOpen && (
                    <>
                        <div>
                            {storagesNetwork.map((storage) => {
                                const active = viewMode === 'storage-network' && selectedStorageNetworkId === storage.id;
                                return (
                                    <div
                                        key={storage.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '8px 11px 8px 14px',
                                            background: active ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent',
                                            borderLeft: active ? '3px solid var(--nj-semantic-color-border-brand-primary-default)' : '3px solid transparent',
                                            fontSize: 14,
                                            fontWeight: active ? 600 : 400,
                                            color: active
                                                ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                                : 'var(--nj-semantic-color-text-neutral-primary-default)',
                                        }}
                                    >
                                        <EditableLabel
                                            value={storage.name}
                                            onSave={(newName) => onStorageNetworkRename(storage.id, newName)}
                                            onClick={() => onStorageNetworkSelect(storage.id)}
                                            isActive={active}
                                            forceEdit={editingStorageId === storage.id}
                                            onEditEnd={() => setEditingStorageId(null)}
                                        />
                                        <div style={{ display: 'flex', gap: 4, opacity: 0.7 }}>
                                            <NJIcon
                                                name="edit"
                                                style={{ fontSize: 18, cursor: 'pointer', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingStorageId(storage.id);
                                                }}
                                            />
                                            <NJIcon
                                                name="delete"
                                                style={{ fontSize: 18, cursor: 'pointer', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onStorageNetworkDelete(storage.id);
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                            <NJButton
                                label="Créer un stockage"
                                emphasis="bold"
                                scale="sm"
                                onClick={onCreateStorageNetwork}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Section 3: Composants (drag-and-drop) */}
            <div style={{ border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', margin: '0 8px 8px', borderRadius: 4, overflow: 'hidden' }}>
                <div
                    onClick={() => setComposantsOpen((o) => !o)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        height: 48,
                        cursor: 'pointer',
                        borderBottom: composantsOpen ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none',
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>Composants</span>
                    <NJIcon name={composantsOpen ? 'expand_less' : 'expand_more'} />
                </div>
                {composantsOpen && (
                    <div style={{ padding: '12px 14px' }}>
                        {/* Sur Stockage réseau : afficher uniquement les pompes */}
                        {viewMode === 'storage-network' && (
                            <>
                                {/* Info badge */}
                                <div style={{ 
                                    padding: '8px 10px', 
                                    marginBottom: 12, 
                                    background: 'var(--nj-semantic-color-background-information-secondary-default)', 
                                    border: '1px solid var(--nj-semantic-color-border-information-default)', 
                                    borderRadius: 4 
                                }}>
                                    <FluidText scale="xs" style={{ color: 'var(--nj-semantic-color-text-information-default)' }}>
                                        💡 Seules les pompes peuvent être ajoutées aux stockages réseau
                                    </FluidText>
                                </div>

                                {/* Pompes uniquement */}
                                <div>
                                    <FluidText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                        Pompes de distribution
                                    </FluidText>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: '#fff', cursor: 'grab' }}>
                                            <img src={pumpIcon} alt="Pompe" style={{ width: 32, height: 32 }} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Sur Centrale : afficher tous les composants */}
                        {viewMode === 'plant' && (
                            <>
                                {/* Départ/Retour */}
                                <div style={{ marginBottom: 16 }}>
                                    <FluidText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                        Départ/Retour
                                    </FluidText>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: '#fff', cursor: 'grab' }}>
                                            <img src={departureIcon} alt="Départ" style={{ width: 32, height: 32 }} />
                                        </div>
                                        <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: '#fff', cursor: 'grab' }}>
                                            <img src={returnIcon} alt="Retour" style={{ width: 32, height: 32 }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Moyens de production */}
                                <div style={{ marginBottom: 16 }}>
                                    <FluidText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                        Moyens de production
                                    </FluidText>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {[
                                            { type: 'Centrifuge', color: 'rgba(25, 118, 210, 0.15)' },
                                            { type: 'Scroll', color: 'rgba(0, 150, 136, 0.15)' },
                                        ].map((chiller) => (
                                            <div
                                                key={chiller.type}
                                                title={chiller.type}
                                                style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: chiller.color, cursor: 'grab' }}
                                            >
                                                <img src={chillerIcon} alt={chiller.type} style={{ width: 32, height: 32 }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Pompes */}
                                <div style={{ marginBottom: 16 }}>
                                    <FluidText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                        Pompes
                                    </FluidText>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: '#fff', cursor: 'grab' }}>
                                            <img src={pumpIcon} alt="Pompe" style={{ width: 32, height: 32 }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Contrats */}
                                <div style={{ marginBottom: 16 }}>
                                    <FluidText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                        Contrats
                                    </FluidText>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {[
                                            { icon: waterIcon, alt: 'Eau' },
                                            { icon: powerIcon, alt: 'Électricité' },
                                            { icon: waterCoolingIcon, alt: 'Eau de refroidissement' },
                                        ].map((contract) => (
                                            <div key={contract.alt} style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: '#fff', cursor: 'grab' }}>
                                                <img src={contract.icon} alt={contract.alt} style={{ width: 32, height: 32 }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Stockage central (drag-and-drop) */}
                                <div>
                                    <FluidText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                        Stockage central
                                    </FluidText>
                                    <div
                                        draggable
                                        onDragStart={(_e) => {
                                            draggedItemRef.current = {
                                                id: `storage-central-drag`,
                                                type: 'storage-central',
                                                label: 'Stockage',
                                                data: {
                                                    entityName: 'StorageTank',
                                                    entityLabel: 'Stockage',
                                                    label: 'Stockage',
                                                    identifier: `STOCK-${Date.now()}`,
                                                    powerLoss: 5000,
                                                    volume: 100,
                                                    maxChargeFlow: 0.1,
                                                    maxDischargeFlow: 0.1,
                                                },
                                            };
                                        }}
                                        style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: 'rgba(103, 58, 183, 0.1)', cursor: 'grab' }}
                                        title="Stockage (à positionner entre prod et pompes)"
                                    >
                                        <img src={storageIcon} alt="Stockage" style={{ width: 32, height: 32 }} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Editable Label Component ────────────────────────────────────────────

interface EditableLabelProps {
    value: string;
    onSave: (newValue: string) => void;
    onClick?: () => void;
    isActive?: boolean;
    forceEdit?: boolean;
    onEditStart?: () => void;
    onEditEnd?: () => void;
}

function EditableLabel({ value, onSave, onClick, isActive, forceEdit, onEditStart, onEditEnd }: EditableLabelProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    useEffect(() => {
        setEditValue(value);
    }, [value]);

    useEffect(() => {
        if (forceEdit && !isEditing) {
            setIsEditing(true);
            onEditStart?.();
        }
    }, [forceEdit, isEditing, onEditStart]);

    const handleSave = useCallback(() => {
        if (editValue.trim() && editValue !== value) {
            onSave(editValue.trim());
        }
        setIsEditing(false);
        onEditEnd?.();
    }, [editValue, value, onSave, onEditEnd]);

    const handleCancel = useCallback(() => {
        setEditValue(value);
        setIsEditing(false);
        onEditEnd?.();
    }, [value, onEditEnd]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    }, [handleSave, handleCancel]);

    if (isEditing) {
        return (
            <input
                autoFocus
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                style={{
                    flex: 1,
                    border: '1px solid var(--nj-semantic-color-border-brand-primary-default)',
                    borderRadius: 2,
                    padding: '2px 6px',
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    backgroundColor: 'white',
                    color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                }}
            />
        );
    }

    return (
        <span
            onClick={() => {
                if (onClick) {
                    onClick();
                }
            }}
            onDoubleClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
            }}
            style={{ flex: 1, cursor: 'pointer' }}
            title="Double-cliquer pour éditer"
        >
            {value}
        </span>
    );
}


// ── Canvas Toolbar Component ────────────────────────────────────────────

function CanvasToolbar({ onSave, onReset, hasUnsavedChanges }: { onSave: () => void; onReset: () => void; hasUnsavedChanges: boolean }) {
    const { zoomIn, zoomOut, fitView } = useReactFlow();
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    return (
        <>
            <div style={{ position: 'absolute', top: 12, right: 16, display: 'flex', gap: 6, zIndex: 10 }}>
                {/* Save button with indicator */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: 'var(--nj-semantic-color-background-neutral-primary-default)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    {hasUnsavedChanges && (
                        <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px', gap: 6 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--nj-semantic-color-border-warning-default)' }} />
                            <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-warning-default)', fontWeight: 600 }}>Non sauvegardé</span>
                        </div>
                    )}
                    <NJIconButton
                        label={hasUnsavedChanges ? "Enregistrer les modifications (Ctrl+S)" : "Tout est sauvegardé"}
                        icon="save"
                        variant={hasUnsavedChanges ? "primary" : "tertiary"}
                        scale="sm"
                        onClick={onSave}
                        isDisabled={!hasUnsavedChanges}
                    />
                    <NJIconButton
                        label="Réinitialiser le graphe"
                        icon="restart_alt"
                        variant="tertiary"
                        scale="sm"
                        onClick={() => setShowResetConfirm(true)}
                    />
                </div>
                {/* Zoom controls */}
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: 'var(--nj-semantic-color-background-neutral-primary-default)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <NJIconButton label="Zoom out" icon="remove" variant="tertiary" scale="sm" onClick={() => zoomOut()} />
                    <NJIconButton label="Réinitialiser" icon="fit_screen" variant="tertiary" scale="sm" onClick={() => fitView()} />
                    <NJIconButton label="Zoom in" icon="add" variant="tertiary" scale="sm" onClick={() => zoomIn()} />
                </div>
            </div>

            {/* Confirmation modal pour réinitialiser */}
            <NJModal isOpen={showResetConfirm}>
                <NJModalContentInformation
                    id="resetGraphModal"
                    iconVariant="danger"
                    centeredFooter={true}
                    title="Réinitialiser le graphe"
                    onClose={() => setShowResetConfirm(false)}
                >
                    <div style={{ marginBottom: 24 }}>
                        <FluidText scale="md">
                            Voulez-vous vraiment réinitialiser le graphe aux valeurs par défaut ? Toutes les modifications non sauvegardées seront perdues.
                        </FluidText>
                    </div>
                    <NJButton
                        label="Annuler"
                        variant="secondary"
                        onClick={() => setShowResetConfirm(false)}
                    />
                    <NJButton
                        label="Réinitialiser"
                        variant="primary"
                        onClick={() => {
                            onReset();
                            setShowResetConfirm(false);
                        }}
                    />
                </NJModalContentInformation>
            </NJModal>
        </>
    );
}

// ── Main Canvas Component ───────────────────────────────────────────────

interface StockageEditorCanvasProps {
    viewMode: ViewMode;
    selectedPlantId: PlantId | null;
    selectedStorageNetworkId: StorageNetworkId | null;
    onPlantSelect: (plantId: PlantId) => void;
    onStorageNetworkSelect: (storageId: StorageNetworkId) => void;
    onCreateStorageNetwork: () => void;
    onStorageNetworkEdit: (storageId: string) => void;
    onStorageNetworkDelete: (storageId: string) => void;
    onStorageNetworkRename: (storageId: string, newName: string) => void;
    storagesNetwork: { id: string; name: string; returnNode?: string; supplyNode?: string }[];
}

function StockageEditorCanvas({
    viewMode,
    selectedPlantId,
    selectedStorageNetworkId,
    onPlantSelect: _onPlantSelect,
    onStorageNetworkSelect: _onStorageNetworkSelect,
    onCreateStorageNetwork: _onCreateStorageNetwork,
    onStorageNetworkEdit,
    onStorageNetworkDelete: _onStorageNetworkDelete,
    onStorageNetworkRename: _onStorageNetworkRename,
    storagesNetwork,
}: StockageEditorCanvasProps) {
    const { nodes, setNodes, edges, setEdges } = useProductionEditor();
    const [editingStorageCentralId, setEditingStorageCentralId] = useState<string | null>(null);
    const [editingProductionLineId, setEditingProductionLineId] = useState<string | null>(null);
    const [deletingNodeId, setDeletingNodeId] = useState<string | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [initialNodesHash, setInitialNodesHash] = useState<string>('');
    const [initialEdgesHash, setInitialEdgesHash] = useState<string>('');


    const deleteNodeMutation = useDeleteNode();

    // Générer un hash simple pour détecter les changements
    const generateHash = useCallback((data: unknown) => {
        return JSON.stringify(data);
    }, []);

    // Load graph based on view mode
    useEffect(() => {
        const storageKey = `stockage-editor-${viewMode}-${selectedPlantId ?? selectedStorageNetworkId}`;
        const savedData = localStorage.getItem(storageKey);

        if (savedData) {
            try {
                const { nodes: savedNodes, edges: savedEdges } = JSON.parse(savedData);
                setNodes(savedNodes);
                setEdges(savedEdges);
                setInitialNodesHash(generateHash(savedNodes));
                setInitialEdgesHash(generateHash(savedEdges));
                setHasUnsavedChanges(false);
            } catch (e) {
                console.error('Erreur lors du chargement des données sauvegardées', e);
                loadDefaultGraph();
            }
        } else {
            loadDefaultGraph();
        }

        function loadDefaultGraph() {
            if (viewMode === 'plant' && selectedPlantId) {
                const { nodes: newNodes, edges: newEdges } = buildGraphForPlant(selectedPlantId);
                setNodes(newNodes);
                setEdges(newEdges);
                setInitialNodesHash(generateHash(newNodes));
                setInitialEdgesHash(generateHash(newEdges));
                setHasUnsavedChanges(false);
            } else if (viewMode === 'storage-network' && selectedStorageNetworkId) {
                const storage = (storagesNetwork || []).find((s) => s.id === selectedStorageNetworkId);
                const { nodes: newNodes, edges: newEdges } = buildGraphForStorageNetwork(
                    selectedStorageNetworkId,
                    storage?.name ?? 'Stockage',
                    storage?.returnNode ?? 'NR0001',
                    storage?.supplyNode ?? 'ND0001'
                );
                setNodes(newNodes);
                setEdges(newEdges);
                setInitialNodesHash(generateHash(newNodes));
                setInitialEdgesHash(generateHash(newEdges));
                setHasUnsavedChanges(false);
            }
        }
    }, [viewMode, selectedPlantId, selectedStorageNetworkId, storagesNetwork, setNodes, setEdges, generateHash]);

    // Détecter les changements
    useEffect(() => {
        if (!nodes || !edges) return;
        const currentNodesHash = generateHash(nodes);
        const currentEdgesHash = generateHash(edges);
        const hasChanges = currentNodesHash !== initialNodesHash || currentEdgesHash !== initialEdgesHash;
        setHasUnsavedChanges(hasChanges);
    }, [nodes, edges, initialNodesHash, initialEdgesHash, generateHash]);

    const saveNodesPosition = useCallback(() => {
        if (!nodes || !edges) return;
        const storageKey = `stockage-editor-${viewMode}-${selectedPlantId ?? selectedStorageNetworkId}`;
        // Nettoyer les callbacks avant de sauvegarder
        const cleanNodes = nodes.map((node) => {
            const { onEditClick, onDeleteClick, onDuplicateClick, ...cleanData } = node.data;
            return {
                ...node,
                data: cleanData,
            };
        });
        const dataToSave = {
            nodes: cleanNodes,
            edges: edges,
            savedAt: new Date().toISOString(),
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        setInitialNodesHash(generateHash(nodes));
        setInitialEdgesHash(generateHash(edges));
        setHasUnsavedChanges(false);
        NJToastService('Modifications sauvegardées', {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
    }, [nodes, edges, viewMode, selectedPlantId, selectedStorageNetworkId, generateHash]);

    const handleNodeEdit = useCallback((nodeId: string) => {
        // Trouver le node pour déterminer son type
        const node = nodes?.find((n) => n.id === nodeId);
        
        // Si c'est une ligne de production (groupe avec entityLabel "Ligne de production")
        if (node?.type === 'group' && node.data.entityLabel === 'Ligne de production') {
            setEditingProductionLineId(nodeId);
        } else {
            // Sinon, éditer le stockage central ou autre
            setEditingStorageCentralId(nodeId);
        }
    }, [nodes]);

    const handleNodeDelete = useCallback((nodeId: string) => {
        setDeletingNodeId(nodeId);
    }, []);

    // Injecter les callbacks dans les nodes avec capture de l'ID
    useEffect(() => {
        setNodes((prevNodes) =>
            (prevNodes || []).map((node) => {
                // Dans le stockage réseau, seuls le tank et les pompes ont des icônes
                const isStorageTank = viewMode === 'storage-network' && node.data.entityName === 'StorageTank';
                const isPumpInStorage = viewMode === 'storage-network' && node.data.entityName === 'VariableSpeedPump';
                const isReturnOrDeparture = viewMode === 'storage-network' && (node.data.entityName === 'Return' || node.data.entityName === 'Departure');
                const isGroupInStorage = viewMode === 'storage-network' && node.data.entityName === 'Group';
                
                // Si c'est un nœud retour/départ/groupe dans le stockage réseau, pas de callbacks
                if (isReturnOrDeparture || isGroupInStorage) {
                    return node;
                }
                
                // Pour le tank du stockage réseau, callback spécifique qui ouvre le formulaire d'édition
                if (isStorageTank && selectedStorageNetworkId) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            onEditClick: () => onStorageNetworkEdit(selectedStorageNetworkId),
                        },
                    };
                }
                
                // Pour les pompes dans le stockage réseau : edit + delete
                if (isPumpInStorage) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            onEditClick: () => handleNodeEdit(node.id),
                            onDeleteClick: () => handleNodeDelete(node.id),
                        },
                    };
                }
                
                // Pour les nodes en mode plant : edit + delete
                if (viewMode === 'plant') {
                    // Les lignes de production (groupes) ont uniquement edit, pas delete
                    const isProductionLine = node.type === 'group' && node.data.entityLabel === 'Ligne de production';
                    
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            onEditClick: () => handleNodeEdit(node.id),
                            // Ne pas ajouter onDeleteClick pour les lignes de production
                            ...(!isProductionLine && { onDeleteClick: () => handleNodeDelete(node.id) }),
                        },
                    };
                }
                
                // Par défaut, retourner le node inchangé
                return node;
            })
        );
    }, [handleNodeEdit, handleNodeDelete, setNodes, viewMode, selectedPlantId, selectedStorageNetworkId, onStorageNetworkEdit]);

    // Raccourci clavier Ctrl+S pour sauvegarder
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (hasUnsavedChanges) {
                    saveNodesPosition();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasUnsavedChanges, saveNodesPosition]);

    // Avertir avant de quitter si changements non sauvegardés
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleReset = useCallback(() => {
        const storageKey = `stockage-editor-${viewMode}-${selectedPlantId ?? selectedStorageNetworkId}`;
        localStorage.removeItem(storageKey);
        
        // Recharger le graphe par défaut
        if (viewMode === 'plant' && selectedPlantId) {
            const { nodes: newNodes, edges: newEdges } = buildGraphForPlant(selectedPlantId);
            setNodes(newNodes);
            setEdges(newEdges);
            setInitialNodesHash(generateHash(newNodes));
            setInitialEdgesHash(generateHash(newEdges));
            setHasUnsavedChanges(false);
        } else if (viewMode === 'storage-network' && selectedStorageNetworkId) {
            const storage = (storagesNetwork || []).find((s) => s.id === selectedStorageNetworkId);
            const { nodes: newNodes, edges: newEdges } = buildGraphForStorageNetwork(
                selectedStorageNetworkId,
                storage?.name ?? 'Stockage',
                storage?.returnNode ?? 'NR0001',
                storage?.supplyNode ?? 'ND0001'
            );
            setNodes(newNodes);
            setEdges(newEdges);
            setInitialNodesHash(generateHash(newNodes));
            setInitialEdgesHash(generateHash(newEdges));
            setHasUnsavedChanges(false);
        }
        
        NJToastService('Graphe réinitialisé aux valeurs par défaut', {
            iconName: 'info',
            dismissAfter: 2000,
        });
    }, [viewMode, selectedPlantId, selectedStorageNetworkId, storagesNetwork, setNodes, setEdges, generateHash]);

    const handleDeleteConfirm = useCallback(() => {
        if (!deletingNodeId) return;

        deleteNodeMutation.mutate(deletingNodeId, {
            onSuccess: () => {
                NJToastService('Élément supprimé', {
                    iconName: 'check_circle',
                    dismissAfter: 2000,
                });
                setDeletingNodeId(null);
                // Remove node from local state
                setNodes((prev) => (prev || []).filter((n) => n.id !== deletingNodeId));
                setEdges((prev) => (prev || []).filter((e) => e.source !== deletingNodeId && e.target !== deletingNodeId));
            },
        });
    }, [deletingNodeId, deleteNodeMutation, setNodes, setEdges]);

    const editingNode = (nodes || []).find((n) => n.id === editingStorageCentralId);
    const editingNodeData = editingNode?.data as {
        identifier?: string; label?: string; powerLoss?: number;
        volume?: number; maxChargeFlow?: number; maxDischargeFlow?: number; pumpGroup?: string;
    } | undefined;
    const deletingNode = (nodes || []).find((n) => n.id === deletingNodeId);

    return (
        <>
            <CanvasToolbar onSave={saveNodesPosition} onReset={handleReset} hasUnsavedChanges={hasUnsavedChanges} />

            <ProductionEditorCanvas
                minHeight="100%"
                nodeTypes={CUSTOM_NODE_TYPES}
                edgeTypes={CUSTOM_EDGE_TYPES}
                nodeFactory={({ id, position, draggedType, label, data }) => {
                    if (draggedType === 'storage-central') {
                        // Find production lines and pump group to auto-connect
                        const productionLines = nodes.filter((n) => n.type === 'group' && n.data.entityLabel === 'Ligne de production');
                        const pumpGroup = nodes.find((n) => n.type === 'group' && n.data.entityLabel === 'Groupe de pompes');

                        let calculatedPosition = position;

                        // If we found production lines and pump group, position storage between them
                        if (productionLines.length > 0 && pumpGroup) {
                            const avgLineX = productionLines.reduce((sum, line) => sum + line.position.x, 0) / productionLines.length;
                            const avgLineY = productionLines.reduce((sum, line) => sum + line.position.y, 0) / productionLines.length;
                            const avgLineWidth = productionLines.reduce((sum, line) => sum + (line.width || 0), 0) / productionLines.length;
                            
                            // Position storage between production lines and pump group
                            calculatedPosition = {
                                x: avgLineX + avgLineWidth + 80,
                                y: avgLineY + 50,
                            };

                            // Create the storage node
                            const storageNode = {
                                id,
                                type: 'asset' as const,
                                position: calculatedPosition,
                                data: {
                                    ...data,
                                    entityName: 'StorageTank',
                                    entityLabel: 'Stockage',
                                    label,
                                },
                                width: 160,
                                height: 160,
                            };

                            // Find the Return node
                            const returnNode = nodes.find((n) => n.type === 'asset' && n.data.entityLabel === 'Retour');

                            // Create edges with new pattern:
                            // A) Return → Storage (left handle, cyan)
                            // B) Storage → Pump group (right handle, blue)
                            // C) Storage → each Production line (left handle, cyan)
                            // D) Each Production line → Storage (s-right handle, blue)
                            const newEdges: Edge[] = [];
                            
                            // A) Retour → Stockage (coude gauche, cyan)
                            if (returnNode) {
                                newEdges.push({
                                    id: `e-${returnNode.id}-${id}`,
                                    source: returnNode.id,
                                    target: id,
                                    type: 'animatedFlowCyan',
                                    sourceHandle: 'right',
                                    targetHandle: 'left',
                                });
                            }

                            // D) Chaque ligne de production → Stockage (coude droit de ligne → coude haut stockage, bleu)
                            productionLines.forEach((line) => {
                                newEdges.push({
                                    id: `e-${line.id}-${id}`,
                                    source: line.id,
                                    target: id,
                                    type: 'animatedFlow',
                                    sourceHandle: 's-right',
                                    targetHandle: 'top',
                                });
                            });

                            // C) Stockage → chaque ligne de production (coude haut stockage → coude gauche ligne, cyan)
                            productionLines.forEach((line) => {
                                newEdges.push({
                                    id: `e-${id}-${line.id}`,
                                    source: id,
                                    target: line.id,
                                    type: 'animatedFlowCyan',
                                    sourceHandle: 'top',
                                    targetHandle: 'left',
                                });
                            });

                            // B) Stockage → Groupe de pompes (coude droit en bas, bleu)
                            if (pumpGroup) {
                                newEdges.push({
                                    id: `e-${id}-${pumpGroup.id}`,
                                    source: id,
                                    target: pumpGroup.id,
                                    type: 'animatedFlow',
                                    sourceHandle: 's-right',
                                    targetHandle: 'left',
                                });
                            }

                            // Add edges to the graph
                            setTimeout(() => {
                                setEdges((prev) => [...(prev || []), ...newEdges]);
                            }, 100);

                            return storageNode;
                        }

                        return {
                            id,
                            type: 'asset',
                            position: calculatedPosition,
                            data: {
                                ...data,
                                entityName: 'StorageTank',
                                entityLabel: 'Stockage',
                                label,
                            },
                            width: 160,
                            height: 160,
                        };
                    }
                    // Default node
                    return {
                        id,
                        type: 'asset',
                        position,
                        data: { label, ...(data ?? {}) },
                    };
                }}
            />

            {/* Storage Central Edit Modal */}
            {editingStorageCentralId && editingNode && (
                <>
                    {/* Overlay background */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 9998,
                        }}
                        onClick={() => setEditingStorageCentralId(null)}
                    />
                    {/* Panel droite */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            height: '100vh',
                            maxHeight: '100vh',
                            width: '500px',
                            backgroundColor: 'white',
                            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
                            zIndex: 9999,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                padding: '12px 24px',
                                borderBottom: '1px solid var(--nj-core-color-grey-200)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <NJIcon name="inventory" style={{ fontSize: 24, color: 'var(--nj-semantic-color-text-brand-primary-default)' }} />
                                <NJHeading as="h3" scale="md">
                                    Stockage central
                                </NJHeading>
                            </div>
                            <button
                                onClick={() => setEditingStorageCentralId(null)}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    lineHeight: 1,
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Body scrollable */}
                        <div style={{ flex: '1 1 0', overflowY: 'auto', padding: '12px 24px 16px', minHeight: 0 }}>
                            {/* Section: Informations générales */}
                            <div style={{ marginBottom: 24 }}>
                                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                                    📝 Informations générales
                                </NJHeading>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div>
                                        <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                            Identifiant *
                                        </FluidText>
                                        <input
                                            type="text"
                                            value={editingNodeData?.identifier || ''}
                                            onChange={(e) =>
                                                setNodes((prev) =>
                                                    (prev || []).map((n) =>
                                                        n.id === editingStorageCentralId
                                                            ? { ...n, data: { ...n.data, identifier: e.target.value } }
                                                            : n
                                                    )
                                                )
                                            }
                                            placeholder="STOCK-001"
                                            style={{
                                                width: '100%',
                                                minHeight: 40,
                                                padding: '10px 12px',
                                                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                                borderRadius: 4,
                                                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                            Nom *
                                        </FluidText>
                                        <input
                                            type="text"
                                            value={editingNodeData?.label || ''}
                                            onChange={(e) =>
                                                setNodes((prev) =>
                                                    (prev || []).map((n) =>
                                                        n.id === editingStorageCentralId
                                                            ? { ...n, data: { ...n.data, label: e.target.value } }
                                                            : n
                                                    )
                                                )
                                            }
                                            placeholder="Stockage"
                                            style={{
                                                width: '100%',
                                                minHeight: 40,
                                                padding: '10px 12px',
                                                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section: Paramètres techniques */}
                            <div style={{ marginBottom: 24 }}>
                                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                                    ⚙️ Paramètres techniques
                                </NJHeading>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div>
                                        <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                            Puissance de déperdition [W]
                                        </FluidText>
                                        <input
                                            type="number"
                                            value={editingNodeData?.powerLoss ?? 0}
                                            onChange={(e) =>
                                                setNodes((prev) =>
                                                    (prev || []).map((n) =>
                                                        n.id === editingStorageCentralId
                                                            ? { ...n, data: { ...n.data, powerLoss: parseFloat(e.target.value) } }
                                                            : n
                                                    )
                                                )
                                            }
                                            placeholder="3000"
                                            style={{
                                                width: '100%',
                                                minHeight: 40,
                                                padding: '10px 12px',
                                                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                            Volume [m³]
                                        </FluidText>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={editingNodeData?.volume ?? 0}
                                            onChange={(e) =>
                                                setNodes((prev) =>
                                                    (prev || []).map((n) =>
                                                        n.id === editingStorageCentralId
                                                            ? { ...n, data: { ...n.data, volume: parseFloat(e.target.value) } }
                                                            : n
                                                    )
                                                )
                                            }
                                            placeholder="300"
                                            style={{
                                                width: '100%',
                                                minHeight: 40,
                                                padding: '10px 12px',
                                                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                            Débit maximum de charge [m³/s]
                                        </FluidText>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={editingNodeData?.maxChargeFlow ?? 0}
                                            onChange={(e) =>
                                                setNodes((prev) =>
                                                    (prev || []).map((n) =>
                                                        n.id === editingStorageCentralId
                                                            ? { ...n, data: { ...n.data, maxChargeFlow: parseFloat(e.target.value) } }
                                                            : n
                                                    )
                                                )
                                            }
                                            placeholder="0.1"
                                            style={{
                                                width: '100%',
                                                minHeight: 40,
                                                padding: '10px 12px',
                                                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                            Débit maximal de décharge [m³/s]
                                        </FluidText>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={editingNodeData?.maxDischargeFlow ?? 0}
                                            onChange={(e) =>
                                                setNodes((prev) =>
                                                    (prev || []).map((n) =>
                                                        n.id === editingStorageCentralId
                                                            ? { ...n, data: { ...n.data, maxDischargeFlow: parseFloat(e.target.value) } }
                                                            : n
                                                    )
                                                )
                                            }
                                            placeholder="0.1"
                                            style={{
                                                width: '100%',
                                                minHeight: 40,
                                                padding: '10px 12px',
                                                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section: Relations */}
                            <div>
                                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                                    🔗 Relations
                                </NJHeading>
                                <div>
                                    <FluidText scale="sm" style={{ display: 'block', marginBottom: 6 }}>
                                        Groupe de pompes de distribution
                                    </FluidText>
                                    <select
                                        value={editingNodeData?.pumpGroup || ''}
                                        onChange={(e) =>
                                            setNodes((prev) =>
                                                (prev || []).map((n) =>
                                                    n.id === editingStorageCentralId
                                                        ? { ...n, data: { ...n.data, pumpGroup: e.target.value } }
                                                        : n
                                                )
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            minHeight: 40,
                                            padding: '10px 12px',
                                            border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                            borderRadius: 4,
                                        }}
                                    >
                                        <option value="">-- Sélectionner --</option>
                                        <option value="Groupe Pompes 1">Groupe Pompes 1</option>
                                        <option value="Groupe Pompes 2">Groupe Pompes 2</option>
                                        <option value="Groupe Pompes 3">Groupe Pompes 3</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Footer (fixed) */}
                        <div
                            style={{
                                padding: '10px 24px',
                                borderTop: '1px solid var(--nj-core-color-grey-200)',
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'flex-end',
                                backgroundColor: 'white',
                                flexShrink: 0,
                            }}
                        >
                            <NJButton label="Annuler" emphasis="subtle" onClick={() => setEditingStorageCentralId(null)} />
                            <NJButton
                                label="Enregistrer"
                                emphasis="bold"
                                onClick={() => {
                                    NJToastService('Stockage mis à jour', {
                                        iconName: 'check_circle',
                                        dismissAfter: 2000,
                                    });
                                    setEditingStorageCentralId(null);
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Production Line Edit Modal */}
            {editingProductionLineId && (() => {
                const productionLine = nodes?.find((n) => n.id === editingProductionLineId);
                if (!productionLine) return null;
                
                // Trouver les chillers et pompes enfants de cette ligne
                const childNodes = nodes?.filter((n) => n.parentId === editingProductionLineId) || [];
                const chillersList = childNodes
                    .filter((n) => n.data.entityName === 'Chiller')
                    .map((n) => String(n.data.label));
                const evaporatorPumpsList = childNodes
                    .filter((n) => n.data.entityName === 'VariableSpeedPump' && String(n.data.entityLabel ?? '').includes('évaporateur'))
                    .map((n) => String(n.data.label));
                const condenserPumpsList = childNodes
                    .filter((n) => n.data.entityName === 'VariableSpeedPump' && String(n.data.entityLabel ?? '').includes('condenseur'))
                    .map((n) => String(n.data.label));
                
                // Charger les données existantes ou utiliser des valeurs par défaut
                const existingData = productionLine.data.productionLineData as ProductionLineFormData | undefined;
                
                return (
                    <ProductionLineEditModal
                        isOpen={true}
                        onClose={() => setEditingProductionLineId(null)}
                        initialData={{
                            identifier: editingProductionLineId,
                            name: String(productionLine.data.label || ''),
                            electricityContract: existingData?.electricityContract || 'Energy_Forum',
                            coolingType: existingData?.coolingType || 'Refroidissement par eau',
                            minFlowHalfLine: existingData?.minFlowHalfLine ?? 50,
                            minFlowFullLine: existingData?.minFlowFullLine ?? 100,
                            maxFlow: existingData?.maxFlow ?? 500,
                            priority: existingData?.priority ?? 1,
                            postProductionShutdown: existingData?.postProductionShutdown ?? false,
                            evaporatorPumpGroups: evaporatorPumpsList,
                            chillers: chillersList,
                            condenserPumpGroups: condenserPumpsList,
                        }}
                        onSubmit={(data) => {
                            // Sauvegarder toutes les données du formulaire
                            setNodes((prev) =>
                                (prev || []).map((n) =>
                                    n.id === editingProductionLineId
                                        ? {
                                            ...n,
                                            data: {
                                                ...n.data,
                                                label: data.name,
                                                productionLineData: data,
                                            },
                                        }
                                        : n
                                )
                            );
                            NJToastService('Ligne de production modifiée', {
                                iconName: 'check_circle',
                                dismissAfter: 2000,
                            });
                            setEditingProductionLineId(null);
                        }}
                    />
                );
            })()}

            {/* Delete Confirmation Modal */}
            <NJModal isOpen={!!deletingNodeId}>
                <NJModalContentInformation
                    iconVariant="danger"
                    centeredFooter={true}
                    title="Confirmation de suppression"
                    onClose={() => setDeletingNodeId(null)}
                >
                    <div style={{ marginBottom: 24 }}>
                        <FluidText scale="md">
                            Êtes-vous sûr de vouloir supprimer <strong>{String(deletingNode?.data.label ?? '')}</strong> ?
                        </FluidText>
                    </div>
                    <NJButton
                        label="Annuler"
                        variant="secondary"
                        onClick={() => setDeletingNodeId(null)}
                        style={{ marginRight: 12 }}
                    />
                    <NJButton
                        label="Je supprime"
                        variant="primary"
                        onClick={handleDeleteConfirm}
                    />
                </NJModalContentInformation>
            </NJModal>
        </>
    );
}

// ── Main Component ──────────────────────────────────────────────────────

export default function StockageEditor() {
    const [viewMode, setViewMode] = useState<ViewMode>('plant');
    const [selectedPlantId, setSelectedPlantId] = useState<PlantId>('cber');
    const [selectedStorageNetworkId, setSelectedStorageNetworkId] = useState<StorageNetworkId | null>(null);
    const [isCreateStorageModalOpen, setIsCreateStorageModalOpen] = useState(false);
    const [editingStorageNetworkId, setEditingStorageNetworkId] = useState<string | null>(null);
    const [deletingStorageNetworkId, setDeletingStorageNetworkId] = useState<string | null>(null);
    const [deletingPlantId, setDeletingPlantId] = useState<PlantId | null>(null);
    const [isCreatePlantModalOpen, setIsCreatePlantModalOpen] = useState(false);
    const [isCreateChillerModalOpen, setIsCreateChillerModalOpen] = useState(false);
    const [isCreatePumpModalOpen, setIsCreatePumpModalOpen] = useState(false);
    const [isCreateContractModalOpen, setIsCreateContractModalOpen] = useState(false);
    const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>('production');
    
    const [plants, setPlants] = useState<{ id: PlantId; label: string }[]>([
        { id: 'cber' as PlantId, label: 'Bercy' },
        { id: 'cauber' as PlantId, label: 'Aubervilliers' },
    ]);

    const { data: storagesNetwork = [] } = useStoragesNetwork();
    const createStorageMutation = useCreateStorageNetwork();
    const updateStorageMutation = useUpdateStorageNetwork();
    const deleteStorageMutation = useDeleteStorageNetwork();

    const handlePlantSelect = useCallback((plantId: PlantId) => {
        setViewMode('plant');
        setSelectedPlantId(plantId);
        setSelectedStorageNetworkId(null);
    }, []);

    const handleStorageNetworkSelect = useCallback((storageId: StorageNetworkId) => {
        setViewMode('storage-network');
        setSelectedStorageNetworkId(storageId);
    }, []);

    const handleCreateStorageNetwork = useCallback(() => {
        setIsCreateStorageModalOpen(true);
    }, []);

    const handleStorageNetworkCreateSubmit = useCallback((data: StorageNetworkFormData) => {
        createStorageMutation.mutate(data, {
            onSuccess: (newStorage) => {
                NJToastService('Stockage réseau créé', {
                    iconName: 'check_circle',
                    dismissAfter: 2000,
                });
                setIsCreateStorageModalOpen(false);
                // Select the new storage
                setViewMode('storage-network');
                setSelectedStorageNetworkId(newStorage.id);
            },
        });
    }, [createStorageMutation]);

    const handleStorageNetworkEdit = useCallback((storageId: string) => {
        setEditingStorageNetworkId(storageId);
    }, []);

    const handleStorageNetworkEditSubmit = useCallback((data: StorageNetworkFormData) => {
        if (!editingStorageNetworkId) return;
        updateStorageMutation.mutate(
            { id: editingStorageNetworkId, data },
            {
                onSuccess: () => {
                    NJToastService('Stockage réseau modifié', {
                        iconName: 'check_circle',
                        dismissAfter: 2000,
                    });
                    setEditingStorageNetworkId(null);
                },
            }
        );
    }, [editingStorageNetworkId, updateStorageMutation]);

    const handleStorageNetworkDelete = useCallback((storageId: string) => {
        setDeletingStorageNetworkId(storageId);
    }, []);

    const confirmStorageNetworkDelete = useCallback(() => {
        if (!deletingStorageNetworkId) return;
        deleteStorageMutation.mutate(deletingStorageNetworkId, {
            onSuccess: () => {
                NJToastService('Stockage réseau supprimé', {
                    iconName: 'check_circle',
                    dismissAfter: 2000,
                });
                setDeletingStorageNetworkId(null);
                // Si on était sur ce stockage, revenir à la première centrale
                if (selectedStorageNetworkId === deletingStorageNetworkId) {
                    setViewMode('plant');
                    setSelectedPlantId('cber');
                    setSelectedStorageNetworkId(null);
                }
            },
        });
    }, [deletingStorageNetworkId, deleteStorageMutation, selectedStorageNetworkId]);

    const handleStorageNetworkRename = useCallback((storageId: string, newName: string) => {
        const storage = storagesNetwork.find((s) => s.id === storageId);
        if (!storage) return;
        
        updateStorageMutation.mutate(
            { id: storageId, data: { ...storage, name: newName } },
            {
                onSuccess: () => {
                    NJToastService('Stockage réseau renommé', {
                        iconName: 'check_circle',
                        dismissAfter: 2000,
                    });
                },
            }
        );
    }, [storagesNetwork, updateStorageMutation]);

    const handlePlantRename = useCallback((plantId: PlantId, newName: string) => {
        setPlants((prev) => prev.map((p) => (p.id === plantId ? { ...p, label: newName } : p)));
        NJToastService('Centrale renommée', {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
    }, []);

    const handlePlantDelete = useCallback((plantId: PlantId) => {
        setDeletingPlantId(plantId);
    }, []);

    const confirmPlantDelete = useCallback(() => {
        if (!deletingPlantId) return;
        
        setPlants((prev) => prev.filter((p) => p.id !== deletingPlantId));
        NJToastService('Centrale supprimée', {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
        setDeletingPlantId(null);
        
        // Si on était sur cette centrale, revenir à la première centrale disponible
        if (selectedPlantId === deletingPlantId) {
            const remaining = plants.filter((p) => p.id !== deletingPlantId);
            if (remaining.length > 0) {
                setSelectedPlantId(remaining[0].id);
            }
        }
    }, [deletingPlantId, selectedPlantId, plants]);

    // Handlers pour les autres créations
    const handlePlantCreate = useCallback((data: any) => {
        NJToastService(`Centrale "${data.name}" créée`, {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
        setIsCreatePlantModalOpen(false);
    }, []);

    const handleChillerCreate = useCallback((data: any) => {
        NJToastService(`Chiller "${data.name}" créé`, {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
        setIsCreateChillerModalOpen(false);
    }, []);

    const handlePumpCreate = useCallback((data: any) => {
        NJToastService(`Pompe "${data.name}" créée`, {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
        setIsCreatePumpModalOpen(false);
    }, []);

    const handleContractCreate = useCallback((data: any) => {
        NJToastService(`Contrat "${data.name}" créé`, {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
        setIsCreateContractModalOpen(false);
    }, []);

    const storagesNetworkList = useMemo(
        () => storagesNetwork.map((s) => ({ id: s.id, name: s.name, returnNode: s.returnNode, supplyNode: s.supplyNode })),
        [storagesNetwork]
    );

    return (
        <>
            <ProductionEditorProvider>
                {/* Barre supérieure avec navigation */}
                <TopBar />

                {/* Barre d'icônes ultra-fine à gauche */}
                <IconBar activeTab={activeSidebarTab} onTabChange={setActiveSidebarTab} />
                
                {/* Panneau secondaire avec le contenu de l'onglet actif */}
                <SecondaryPanel
                    activeTab={activeSidebarTab}
                    viewMode={viewMode}
                    selectedPlantId={selectedPlantId}
                    selectedStorageNetworkId={selectedStorageNetworkId}
                    onPlantSelect={handlePlantSelect}
                    onStorageNetworkSelect={handleStorageNetworkSelect}
                    onCreateStorageNetwork={handleCreateStorageNetwork}
                    onStorageNetworkEdit={handleStorageNetworkEdit}
                    onStorageNetworkDelete={handleStorageNetworkDelete}
                    onStorageNetworkRename={handleStorageNetworkRename}
                    onPlantRename={handlePlantRename}
                    onPlantDelete={handlePlantDelete}
                    storagesNetwork={storagesNetworkList}
                    plants={plants}
                    setIsCreatePlantModalOpen={setIsCreatePlantModalOpen}
                />

                <div style={{ display: 'flex', minHeight: 'calc(100vh - 96px)', marginTop: 56, marginBottom: 40, backgroundColor: 'var(--nj-semantic-color-background-neutral-secondary-default)', paddingLeft: 348 }}>
                    <div style={{ flex: 1, position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                        <StockageEditorCanvas
                            viewMode={viewMode}
                            selectedPlantId={selectedPlantId}
                            selectedStorageNetworkId={selectedStorageNetworkId}
                            onPlantSelect={handlePlantSelect}
                            onStorageNetworkSelect={handleStorageNetworkSelect}
                            onCreateStorageNetwork={handleCreateStorageNetwork}
                            onStorageNetworkEdit={handleStorageNetworkEdit}
                            onStorageNetworkDelete={handleStorageNetworkDelete}
                            onStorageNetworkRename={handleStorageNetworkRename}
                            storagesNetwork={storagesNetworkList}
                        />
                    </div>
                </div>

                {/* Barre inférieure avec liens */}
                <Footer />
            </ProductionEditorProvider>

            {/* Storage Network Create Modal */}
            <StorageNetworkEditModal
                isOpen={isCreateStorageModalOpen}
                onClose={() => setIsCreateStorageModalOpen(false)}
                onSubmit={handleStorageNetworkCreateSubmit}
            />

            {/* Storage Network Edit Modal */}
            {editingStorageNetworkId && (
                <StorageNetworkEditModal
                    isOpen={true}
                    onClose={() => setEditingStorageNetworkId(null)}
                    initialData={storagesNetwork.find((s) => s.id === editingStorageNetworkId)}
                    onSubmit={handleStorageNetworkEditSubmit}
                />
            )}

            {/* Storage Network Delete Confirmation Modal */}
            <NJModal isOpen={!!deletingStorageNetworkId}>
                <NJModalContentInformation
                    iconVariant="danger"
                    centeredFooter={true}
                    title="Confirmation de suppression"
                    onClose={() => setDeletingStorageNetworkId(null)}
                >
                    <div style={{ marginBottom: 24 }}>
                        <FluidText scale="md">
                            Êtes-vous sûr de vouloir supprimer le stockage réseau{' '}
                            <strong>{storagesNetwork.find((s) => s.id === deletingStorageNetworkId)?.name}</strong> ?
                        </FluidText>
                    </div>
                    <NJButton
                        label="Annuler"
                        variant="secondary"
                        onClick={() => setDeletingStorageNetworkId(null)}
                        style={{ marginRight: 12 }}
                    />
                    <NJButton
                        label="Je supprime"
                        variant="primary"
                        onClick={confirmStorageNetworkDelete}
                    />
                </NJModalContentInformation>
            </NJModal>

            {/* Plant Delete Confirmation Modal */}
            <NJModal isOpen={!!deletingPlantId}>
                <NJModalContentInformation
                    iconVariant="danger"
                    centeredFooter={true}
                    title="Confirmation de suppression"
                    onClose={() => setDeletingPlantId(null)}
                >
                    <div style={{ marginBottom: 24 }}>
                        <FluidText scale="md">
                            Êtes-vous sûr de vouloir supprimer la centrale{' '}
                            <strong>{plants.find((p) => p.id === deletingPlantId)?.label}</strong> ?
                        </FluidText>
                    </div>
                    <NJButton
                        label="Annuler"
                        variant="secondary"
                        onClick={() => setDeletingPlantId(null)}
                        style={{ marginRight: 12 }}
                    />
                    <NJButton
                        label="Je supprime"
                        variant="primary"
                        onClick={confirmPlantDelete}
                    />
                </NJModalContentInformation>
            </NJModal>

            {/* Plant Create Modal */}
            <PlantCreateModal
                isOpen={isCreatePlantModalOpen}
                onClose={() => setIsCreatePlantModalOpen(false)}
                onSubmit={handlePlantCreate}
            />

            {/* Chiller Create Modal */}
            <ChillerCreateModal
                isOpen={isCreateChillerModalOpen}
                onClose={() => setIsCreateChillerModalOpen(false)}
                onSubmit={handleChillerCreate}
                plantId={selectedPlantId || 'cber'}
            />

            {/* Pump Create Modal */}
            <PumpCreateModal
                isOpen={isCreatePumpModalOpen}
                onClose={() => setIsCreatePumpModalOpen(false)}
                onSubmit={handlePumpCreate}
                plantId={selectedPlantId || 'cber'}
            />

            {/* Contract Create Modal */}
            <ContractCreateModal
                isOpen={isCreateContractModalOpen}
                onClose={() => setIsCreateContractModalOpen(false)}
                onSubmit={handleContractCreate}
            />

            <NJToastContainer />
        </>
    );
}
